import React, { FC } from "react";
import { Box } from "@mui/material";
import { IconDownload, IconEye, IconTrash } from "../../utils/icons";
import { badgeLabelOf } from "./attachmentKinds";

interface Props {
    name: string;
    thumbnail?: string;
    subtitle: string;
    onPreview?: () => void;
    onDownload?: () => void;
    onRemove?: () => void;
    busy?: boolean;
    muted?: boolean;
}

const ACTION_CLASSES =
    "shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-md text-text-2 transition-colors disabled:opacity-40";

export const AttachmentRow: FC<Props> = ({
    name,
    thumbnail,
    subtitle,
    onPreview,
    onDownload,
    onRemove,
    busy,
    muted,
}) => (
    <Box
        className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl border border-border-token bg-surface transition-colors hover:border-[var(--accent)] ${
            muted ? "opacity-80" : ""
        }`}
    >
        <button type="button" onClick={onPreview} aria-label={`Preview ${name}`} className="shrink-0">
            {thumbnail ? (
                <img
                    src={thumbnail}
                    alt=""
                    loading="lazy"
                    className="w-10 h-10 rounded-lg object-cover border border-border-token bg-surface-2"
                />
            ) : (
                <span className="w-10 h-10 rounded-lg border border-[#EAD9B8] bg-tint flex items-center justify-center text-[9.5px] font-bold tracking-wide text-accent-text">
                    {badgeLabelOf(name)}
                </span>
            )}
        </button>

        <button type="button" onClick={onPreview} title={name} className="flex-1 min-w-0 text-left">
            <span className="block truncate text-[13.5px] font-medium text-text-1 group-hover:text-accent-text transition-colors">
                {name}
            </span>
            <span className="block truncate text-[11.5px] text-muted">{subtitle}</span>
        </button>

        <button
            type="button"
            onClick={onPreview}
            aria-label={`View ${name}`}
            title="View"
            className={`${ACTION_CLASSES} hover:bg-surface-2 hover:text-accent-text`}
        >
            <IconEye width={15} height={15} stroke="currentColor" />
        </button>
        {onDownload && (
            <button
                type="button"
                onClick={onDownload}
                aria-label={`Download ${name}`}
                title="Download"
                className={`${ACTION_CLASSES} hover:bg-surface-2 hover:text-accent-text`}
            >
                <IconDownload width={15} height={15} stroke="currentColor" />
            </button>
        )}
        {onRemove && (
            <button
                type="button"
                disabled={busy}
                onClick={onRemove}
                aria-label={`Remove ${name}`}
                title="Remove"
                className={`${ACTION_CLASSES} hover:bg-[var(--down-bg)] hover:text-[var(--down)]`}
            >
                <IconTrash width={14} height={14} stroke="currentColor" />
            </button>
        )}
    </Box>
);
